import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'

export type OrgLevelVariantRow = {
  id: number
  vertical_id: number | null
  vertical_unique_id?: string | null
  vertical_system_code?: string | null
  parent_id?: number | null
  vertical_name?: string | null
  name?: string | null
  variant_name?: string | null
  variant_name_text?: string | null
  /** Vertical (catalog) order — from the vertical row */
  order?: number
  /** Sibling order under the same parent within this vertical */
  sort_order?: number
  /** Set server-side from vertical tier; retained for API compatibility */
  variant_type: number
  /** Stable slug within the org; system tiers use root-org-lvl-variant, tenant-lvl-variant, branch-lvl-variant */
  unique_id: string
  /**
   * Immutable tier identity — set once at creation, never re-derived from sort_order.
   * Seeded from unique_id for existing rows. Survives tier insertions.
   */
  tier_code?: string | null
  is_system: boolean
  /** Catalog level variant; display name only for system catalog tiers. */
  system_managed?: boolean
  active: number
  created_on?: string
  updated_on?: string
}

/** System catalog tier `unique_id` values (fixed; align with backend SYSTEM_LEVEL_VARIANT_UNIQUE_ID). */
export const CATALOG_SYSTEM_TIER_LEVEL_VARIANT_UNIQUE_IDS = new Set<string>([
  'root-org-lvl-variant',
  'head-tenant-lvl-variant',
  'tenant-lvl-variant',
  'branch-lvl-variant',
])

/** Default branch tier level-variant `unique_id` in the org catalog (sidebar + branch directory filter). */
export const BRANCH_CATALOG_LEVEL_VARIANT_UNIQUE_ID = 'branch-lvl-variant'

function unwrapListResults(data: unknown): OrgLevelVariantRow[] {
  const payload = data as Record<string, unknown>
  const inner =
    payload && typeof payload === 'object' && 'data' in payload && payload.data
      ? (payload.data as Record<string, unknown>)
      : payload
  const rows = inner?.results
  return Array.isArray(rows) ? (rows as OrgLevelVariantRow[]) : []
}

export const orgLevelVariantsApi = {
  async list(
    serviceInstanceRef: string | number,
    options?: { includeInactive?: boolean },
  ): Promise<OrgLevelVariantRow[]> {
    const { data } = await api.get<unknown>(API_PATHS.organisation.levelVariants.list, {
      params: {
        service_instance: serviceInstanceRef,
        page_size: 200,
        include_inactive: options?.includeInactive ? 1 : undefined,
      },
    })
    return unwrapListResults(data)
  },

  async create(payload: {
    vertical_id: number
    variant_name: string
    variant_type?: number
    /** Organisation-unique slug; optional — backend defaults from display name */
    unique_id?: string
    /**
     * Immutable tier identity slug. Optional — defaults to unique_id if omitted.
     * Set once at creation; backend never re-derives it.
     */
    tier_code?: string
    sort_order?: number
    parent_id?: number | null
  }): Promise<void> {
    const body: Record<string, unknown> = {
      vertical_id: payload.vertical_id,
      variant_name: payload.variant_name,
    }
    if (payload.variant_type !== undefined) {
      body.variant_type = payload.variant_type
    }
    if (payload.unique_id != null && String(payload.unique_id).trim() !== '') {
      body.unique_id = String(payload.unique_id).trim()
    }
    if (payload.tier_code != null && String(payload.tier_code).trim() !== '') {
      body.tier_code = String(payload.tier_code).trim()
    }
    if (payload.sort_order !== undefined) {
      body.sort_order = payload.sort_order
    }
    if (payload.parent_id !== undefined && payload.parent_id !== null) {
      body.parent_id = payload.parent_id
    }
    await api.post(API_PATHS.organisation.levelVariants.create, body)
  },

  async update(
    id: string | number,
    payload: {
      variant_name: string
      unique_id?: string
      sort_order?: number
      parent_id?: number | null
    },
  ): Promise<void> {
    const body: Record<string, unknown> = {
      variant_name: payload.variant_name,
    }
    if (payload.unique_id !== undefined && String(payload.unique_id).trim() !== '') {
      body.unique_id = String(payload.unique_id).trim()
    }
    // tier_code is intentionally NOT sent on update — it is immutable after creation.
    if (payload.sort_order !== undefined) {
      body.sort_order = payload.sort_order
    }
    if (payload.parent_id !== undefined) {
      body.parent_id = payload.parent_id
    }
    await api.patch(API_PATHS.organisation.levelVariants.update(id), body)
  },

  async remove(id: string | number): Promise<void> {
    await api.delete(API_PATHS.organisation.levelVariants.remove(id))
  },

  async hide(
    id: string | number,
    payload: {
      variant_name: string
      unique_id?: string
      sort_order?: number
      parent_id?: number | null
    },
  ): Promise<void> {
    const body: Record<string, unknown> = {
      variant_name: payload.variant_name,
      active: 0,
    }
    if (payload.unique_id !== undefined && String(payload.unique_id).trim() !== '') {
      body.unique_id = String(payload.unique_id).trim()
    }
    if (payload.sort_order !== undefined) {
      body.sort_order = payload.sort_order
    }
    if (payload.parent_id !== undefined) {
      body.parent_id = payload.parent_id
    }
    await api.patch(API_PATHS.organisation.levelVariants.update(id), body)
  },

  async unhide(
    id: string | number,
    payload: {
      variant_name: string
      unique_id?: string
      sort_order?: number
      parent_id?: number | null
    },
  ): Promise<void> {
    const body: Record<string, unknown> = {
      variant_name: payload.variant_name,
      active: 1,
    }
    if (payload.unique_id !== undefined && String(payload.unique_id).trim() !== '') {
      body.unique_id = String(payload.unique_id).trim()
    }
    if (payload.sort_order !== undefined) {
      body.sort_order = payload.sort_order
    }
    if (payload.parent_id !== undefined) {
      body.parent_id = payload.parent_id
    }
    await api.patch(API_PATHS.organisation.levelVariants.update(id), body)
  },
}
